// Página principal do Módulo de Parcerias
import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { 
  Handshake, Users, 
  BarChart3, AlertCircle, CheckCircle,
  Plus, Search, Key, Euro
} from 'lucide-react';
import api from '../../services/api';

// Sub-componentes
import DashboardParcerias from './DashboardParcerias';
import GestaoParcerias from './GestaoParcerias';
import RegistoVales from './RegistoVales';
import ConsultaVales from './ConsultaVales';
import GestaoUtilizadoresParceria from './GestaoUtilizadoresParceria';
import InfoParceriasAtivas from './InfoParceriasAtivas';
import RelatorioCustos from './RelatorioCustos';
import MinhaParceria from './MinhaParceria';
import MinhaContaTab from '../MinhaContaTab';

const ParceriasModule = ({ user, defaultTab }) => {
  const isAdmin = user?.role === 'admin';
  const isResponsavel = user?.role === 'responsavel';
  const isColaborador = user?.role === 'colaborador';
  const isAdminParceria = user?.role === 'admin_parceria';
  const isColaboradorParceria = user?.role === 'colaborador_parceria';
  
  const isJaneira = isAdmin || isResponsavel || isColaborador;
  const isParceria = isAdminParceria || isColaboradorParceria;
  const canManage = isAdmin || isResponsavel;
  const canManageParceria = isAdmin || isResponsavel || isAdminParceria;

  const [activeTab, setActiveTab] = useState(
    defaultTab || (isAdmin ? 'dashboard' : 
    isParceria ? 'minha-parceria' : 'info-parcerias')
  );

  useEffect(() => {
    if (defaultTab) setActiveTab(defaultTab);
  }, [defaultTab]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await api.get('/parcerias/config');
      setConfig(response.data);
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
    } finally {
      setLoading(false);
    }
  };

  // Tabs disponíveis baseadas no perfil
  const getTabs = () => {
    if (isParceria) {
      // Utilizadores de parceria veem apenas operações da sua parceria
      return [
        { id: 'minha-parceria', label: 'A Minha Parceria', icon: Handshake },
        { id: 'registar', label: 'Registar Vale', icon: Plus },
        { id: 'validar', label: 'Validar Vale', icon: CheckCircle },
        { id: 'consultar', label: 'Consultar', icon: Search },
        ...(isAdminParceria ? [
          { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
          { id: 'utilizadores', label: 'Utilizadores', icon: Users }
        ] : []),
        { id: 'minha-conta', label: 'Minha Conta', icon: Key }
      ];
    }
    
    // Utilizadores Janeira
    const tabs = [];
    
    if (isAdmin) {
      // Admin: acesso completo à gestão de parcerias
      tabs.push(
        { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
        { id: 'consultar', label: 'Consultar', icon: Search },
        { id: 'parcerias', label: 'Parcerias', icon: Handshake },
        { id: 'custos', label: 'Relatório Custos', icon: Euro },
        { id: 'utilizadores', label: 'Utilizadores', icon: Users },
      );
    } else {
      // Colaboradores e Responsáveis: apenas o essencial
      tabs.push(
        { id: 'info-parcerias', label: 'Parcerias Ativas', icon: Handshake },
        { id: 'consultar', label: 'Consultar', icon: Search },
      );
    }
    
    return tabs;
  };

  const tabs = getTabs();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parcerias-module">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Handshake className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-lg font-semibold">Parcerias Janeira</h1>
            <p className="text-sm text-muted-foreground">
              Gestão de parceiros, vales externos e benefícios associados.
            </p>
          </div>
        </div>
        
        {config?.modo_contingencia_ativo && (
          <Badge variant="warning" className="flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            Modo Contingência Ativo
          </Badge>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="flex flex-wrap gap-1">
          {tabs.map(tab => (
            <TabsTrigger 
              key={tab.id} 
              value={tab.id}
              className="flex items-center gap-2"
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Dashboard */}
        <TabsContent value="dashboard">
          <DashboardParcerias user={user} />
        </TabsContent>

        {/* A Minha Parceria - para parceiros */}
        <TabsContent value="minha-parceria">
          <MinhaParceria />
        </TabsContent>

        {/* Registar Vale */}
        <TabsContent value="registar">
          <RegistoVales 
            user={user} 
            modo="registar" 
            contingenciaAtiva={config?.modo_contingencia_ativo}
          />
        </TabsContent>

        {/* Validar Vale - para contingência */}
        <TabsContent value="validar">
          <RegistoVales 
            user={user} 
            modo="validar"
            contingenciaAtiva={config?.modo_contingencia_ativo}
          />
        </TabsContent>

        {/* Consultar */}
        <TabsContent value="consultar">
          <ConsultaVales user={user} />
        </TabsContent>

        {/* Info Parcerias Ativas - Colaboradores */}
        <TabsContent value="info-parcerias">
          <InfoParceriasAtivas user={user} />
        </TabsContent>

        {/* Gestão de Parcerias - Admin/Responsável */}
        {canManage && (
          <TabsContent value="parcerias">
            <GestaoParcerias user={user} />
          </TabsContent>
        )}

        {/* Relatório de Custos - Admin/Responsável */}
        {canManage && (
          <TabsContent value="custos">
            <RelatorioCustos user={user} />
          </TabsContent>
        )}

        {/* Gestão de Utilizadores */}
        {canManageParceria && (
          <TabsContent value="utilizadores">
            <GestaoUtilizadoresParceria user={user} />
          </TabsContent>
        )}

        {/* Minha Conta - Utilizadores de Parceria */}
        {isParceria && (
          <TabsContent value="minha-conta">
            <MinhaContaTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default ParceriasModule;
